import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import MyTable from "../components/MyTable";
import MyBreakcrumbs from "../components/MyBreadcrumbs";
import AddServicePage from "../components/addservicemodel";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { serviceFetchDoctorList } from "../services/api";
import {
  getClinicIdFromToken,
  getToken,
  getValueFromToken,
} from "../utils/tokenUtils";
import SearchInput from "../components/SearchInput";
import { motion, AnimatePresence } from "framer-motion";
import { FiPlus } from "react-icons/fi";
import moment from "moment";
import {
  serviceDeleteServices,
  serviceFetchClinicId,
} from "../services/profileApi";
import { showToast } from "../utils/toastUtils";
import CustomSelect from "../components/CustomSelect";
import { Box, Typography } from "@mui/material";
import ConfirmBox from "../components/ConfirmBox";
import { formatDateGlobal } from "../utils/commonUtils";

const Service = () => {
  const navigate = useNavigate();
  const [filterInput, setFilterInput] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorsForModal, setDoctorsForModal] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("all");
  const [reports, setReports] = useState([]);
  const [error, setError] = useState(null);

  const [decodedClinicId, setDecodedClinicId] = useState(null);
  const [allServiceData, setAllServiceData] = useState([]);
  const [showServiceData, setShowServiceData] = useState([]);
  const [response, setResponse] = useState("");
  const [ShowServiceItem, setServiceItem] = useState(null);

  const [showAddServicePage, setShowAddServicePage] = useState(false); // State to control AddServicePage visibility
  const [deleteId, setDeleteId] = useState(null); // Add this state to store the id of the item to be deleted

  const handleOpenAddServicePage = () => {
    setShowAddServicePage(true); // Open the AddServicePage when the button is clicked
    setServiceItem(null);
  };

  const handleCloseAddServicePage = () => {
    setShowAddServicePage(false); // Close the AddServicePage
  };

  const handleEdit = (data) => {
    setServiceItem(data);
  };

  const handleCopy = (data) => {
    setServiceItem({ ...data, service_item_id: null });
  };

  const handleDelete = async (id, event) => {
    try {
      const token = localStorage.getItem("token");
      const decodedToken = jwtDecode(token);
      setDecodedClinicId(decodedToken.clinic_id);

      const response = await serviceDeleteServices(id);
      if (response?.status) {
        showToast("Item deleted successfully.", "success");
        fetchClinicId();
      }
    } catch (error) {
      setError("Failed to delete item. Please try again later.");
    } finally {
    }
  };

  const fetchDoctors = async () => {
    const userId = getValueFromToken("user_id");

    try {
      const response = await serviceFetchDoctorList(userId, true);
      const doctorsData = response?.data;
      if (
        doctorsData &&
        doctorsData?.doctors &&
        doctorsData?.doctors?.length > 0
      ) {
        const allDoctorsOption = {
          doctor_id: "all",
          first_name: "All Doctors",
          last_name: "",
        };

        // Ensure "All Doctors" option is added only once at the beginning
        setDoctors([allDoctorsOption, ...doctorsData.doctors]);
        setDoctorsForModal(doctorsData.doctors);

        // Optionally set the selectedDoctor to 'all'
        setSelectedDoctor("all");
      } else {
        // Navigate if the doctors array is empty
        navigate("/clinic/doctor?addDoctor=true");
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
      // Handle error or set a state to display an error message
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, [navigate]);

  const doctorIdToName = {};
  doctors.forEach((doctor) => {
    doctorIdToName[
      doctor.doctor_id
    ] = `${doctor.first_name} ${doctor.last_name}`;
  });

  const formatDate = (dateString) => {
    return moment(dateString).format("DD-MM-YYYY");
  };

  const fetchClinicId = async () => {
    try {
      const token = localStorage.getItem("token");
      const decodedToken = jwtDecode(token);
      setDecodedClinicId(decodedToken.clinic_id); // Save decoded clinic_id
      const response = await serviceFetchClinicId(decodedToken.clinic_ids[0]);
      if (response?.status) {
        const data = response.data.doctorDetails;
        setAllServiceData(data);
        setShowServiceData(data);

        const transformedclininc = data?.clinic?.map((clinic) => ({
          ...clinic,
          name: `${clinic.name}`,
        }));

        setReports(transformedclininc);
      }
    } catch (error) {
      setError(error.message);
      console.error("Error fetching Services list:", error);
    }
  };

  useEffect(() => {
    // Service details get api//
    fetchClinicId();
  }, []);

  useEffect(() => {
    if (selectedDoctor) {
      let filteredServices;
      if (selectedDoctor === "all") {
        filteredServices = allServiceData;
      } else {
        filteredServices = allServiceData.filter(
          (service) => Number(service.doctor_id) === Number(selectedDoctor)
        );
      }
      setShowServiceData(filteredServices);
    }
  }, [selectedDoctor]);

  const columns = [
    { Header: " Name", accessor: "name" },
    {
      Header: "Doctor",
      accessor: "name",
      id: "doctoname",
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography ml={1}>
            {row.original.doctor_id === -1
              ? "All Doctors"
              : row.original.doctor_name}
          </Typography>
        </Box>
      ),
    },
    {
      Header: "Price",
      accessor: "default_price",
      Cell: ({ value }) => Number(value).toFixed(2), // Using Cell to customize rendering for this column
    },
    {
      Header: "Start date",
      id: "start_date",
      accessor: (row) => formatDateGlobal(row.start_date),
    },
    {
      Header: "End Date",
      id: "end_date",
      accessor: (row) =>
        row.end_date ? formatDateGlobal(row.end_date) : "Never",
    },
    {
      Header: "Action",
      accessor: "",
      Cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <ConfirmBox
            title="Confirm to submit"
            message="Are you sure you want to delete this item?"
            onConfirm={() => handleDelete(row.original.service_item_id)}
          >
            <span className="text-white bg-red-500 hover:bg-red-600 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center me-2 dark:bg-red-500 dark:hover:bg-red-600 dark:focus:ring-red-600 cursor-pointer">
              <DeleteIcon className="!w-5 !h-5" />
            </span>
          </ConfirmBox>

          {/* Edit icon */}
          <span
            onClick={() => {
              handleOpenAddServicePage();
              handleEdit(row.original);
            }}
            className="text-white bg-blue-500 hover:bg-blue-600 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center me-2 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-blue-600 cursor-pointer"
          >
            <ModeEditIcon className="!w-5 !h-5" />
          </span>

          {/* Copy icon */}
          <span
            className="text-white bg-gray-500 hover:bg-gray-600 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-full text-sm p-2.5 text-center inline-flex items-center me-2 dark:bg-gray-500 dark:hover:bg-gray-600 dark:focus:ring-gray-600 cursor-pointer"
            onClick={() => {
              handleOpenAddServicePage();
              handleCopy(row.original);
            }}
          >
            <ContentCopyIcon className="!w-5 !h-5" />
          </span>
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    {
      label: "Settings",
      href: "#",

      class: "text-[#2E37A4]",
    },
    { label: "Services", class: "text-[#2E37A4]" },
  ];

  const handleFilterChange = (event) => {
    const value = event.target.value || "";
    setFilterInput(value);
  };

  //

  return (
    <>
      {/* other component */}
      <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 py-4 sm:py-6 md:pl-24 ">
        <div className="pt-2 pb-6">
          <MyBreakcrumbs data={breadcrumbs} />
        </div>

        <div className="flex ">
          <div className="bg-white rounded-xl shadow-md shadow-gray-200 overflow-hidden w-full">
            <div className="px-5 py-3 flex flex-wrap md:flex-nowrap justify-between items-center gap-y-5">
              <div className=" w-3/12">
                <CustomSelect
                  value={selectedDoctor}
                  isSearchable={true}
                  isClearable={false}
                  isDisabled={false}
                  onChange={(e) => setSelectedDoctor(e.value)}
                  options={doctors.map((doctor) => ({
                    value: doctor.doctor_id,
                    label: `${doctor.first_name} ${doctor.last_name}`,
                  }))}
                  className={" w-full form_select"}
                />
              </div>

              <div className="inline-flex">
                <SearchInput
                  filterInput={filterInput}
                  handleFilterChange={handleFilterChange}
                />
              </div>
            </div>

            <div className="overflow-auto ">
              <MyTable
                columns={columns}
                data={showServiceData}
                filterInput={filterInput}
                handleFilterChange={handleFilterChange}
              />
            </div>

            <button
              onClick={handleOpenAddServicePage}
              type="button"
              className="text-2xl p-0 fixed right-3 bottom-3 flex items-center justify-center text-white bg-blue-700 rounded-full w-12 h-12 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 focus:outline-none dark:focus:ring-blue-800"
            >
              <FiPlus />
              <span className="sr-only">Add Service</span>
            </button>
            {showAddServicePage && (
              <AddServicePage
                onClose={handleCloseAddServicePage}
                ShowServiceItem={ShowServiceItem}
                fetchClinicId={fetchClinicId}
              />
            )}
            {response && <p>Response: {response}</p>}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selectedAppointment && (
          <motion.div
            initial={{ opacity: 1, right: "-500px" }}
            animate={{ opacity: 1, right: "0px" }}
            exit={{ opacity: 1, right: "-500px" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="bg-white shadow-md rounded-none fixed right-0 top-0 h-full  z-10 w-full lg:w-[500px] xl:w-[500px] 2xl:w-[500px]"
          ></motion.div>
        )}
      </AnimatePresence>

      {/* other component */}
    </>
  );
};

export default Service;
